import type { ReactNode } from 'react';
import { TaskLanguageSelect, TaskSetup } from '@/features/contribution-kit';
import type { Sector } from '@/types/theme.types';
import type { InitialDataCopy } from '../initial-data-copy';
import type { InitialDataLanguage, InitialDataMode } from '../types';
import { SectorPicker } from './SectorPicker';
import { RecordingDeviceFields, type RecordingDeviceSelection } from '@/features/recording-device-metadata';

type InitialDataSetupProps = {
  copy: InitialDataCopy;
  mode: InitialDataMode;
  languages: InitialDataLanguage[];
  isLoadingLanguages: boolean;
  language: string;
  recordingDevice: RecordingDeviceSelection;
  sectors: Sector[];
  isLoadingSectors: boolean;
  sectorsError: boolean;
  sectorId: string;
  stats: Array<{ value: string; label: string; tone?: 'success' }>;
  canStart: boolean;
  isPreparing: boolean;
  /** Collector context, offer to resume: above the form. */
  notices?: ReactNode;
  /** Where the session is collected (spec 004); required before starting. */
  placeStep: ReactNode;
  onLanguageChange: (code: string) => void;
  onRecordingDeviceChange: (device: RecordingDeviceSelection) => void;
  onSectorChange: (sectorId: string) => void;
  onRetry: () => void;
  onBack: () => void;
  onStart: () => void;
};

/** Before an "Audio initial" / "Texte initial" session: place, language, sector, rules. */
export function InitialDataSetup(props: InitialDataSetupProps) {
  const { copy, mode } = props;
  const isAudio = mode === 'audio';
  const instructions = isAudio
    ? [copy.audioInstruction1, copy.audioInstruction2, copy.audioInstruction3, copy.audioInstruction4]
    : [copy.textInstruction1, copy.textInstruction2, copy.textInstruction3, copy.textInstruction4];

  return (
    <TaskSetup
      task={isAudio ? 'answerAudio' : 'answerText'}
      title={isAudio ? copy.audioTitle : copy.textTitle}
      description={isAudio ? copy.audioSubtitle : copy.textSubtitle}
      stats={props.stats}
      instructions={instructions}
      notices={props.notices}
      startLabel={copy.start}
      onStart={props.onStart}
      startDisabled={!props.canStart}
      starting={props.isPreparing}
      onBack={props.onBack}
    >
      {props.placeStep}
      {isAudio ? (
        <RecordingDeviceFields copy={copy.recordingDevice} value={props.recordingDevice} onChange={props.onRecordingDeviceChange} idPrefix="initial-data-recording-device" />
      ) : null}
      <div className="space-y-2">
        <label htmlFor="initial-data-language" className="text-sm font-medium text-foreground">
          {copy.languageLabel}
        </label>
        {/* The wrapper carries the test id the e2e suite clicks. */}
        <div data-testid="initial-data-language">
          <TaskLanguageSelect
            id="initial-data-language"
            value={props.language}
            onChange={props.onLanguageChange}
            languages={props.languages}
            isLoading={props.isLoadingLanguages}
            placeholder={copy.languagePlaceholder}
          />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">{copy.sectorLabel}</p>
        <p className="text-xs leading-5 text-muted-foreground">{copy.sectorHint}</p>
        <SectorPicker
          copy={copy}
          sectors={props.sectors}
          selectedId={props.sectorId}
          isLoading={props.isLoadingSectors}
          hasError={props.sectorsError}
          onSelect={props.onSectorChange}
          onRetry={props.onRetry}
        />
      </div>
    </TaskSetup>
  );
}
